import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Radio,
  RadioGroup,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  CircularProgress,
  TextField,
  Box,
  Grid,
  Typography,
  IconButton,
  Snackbar,
  Alert,
  InputAdornment,
  Card,
  CardContent,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { Modal as BootstrapModal } from "react-bootstrap";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import "jspdf-autotable";
import { jsPDF } from "jspdf";
import InfoIcon from "@mui/icons-material/Info";
import { loadRazorpayScript } from "../../../utils/loadRazorpay";

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: "20px",
  backgroundColor: "#f9fafb",
}));

const StyledHeader = styled(Box)(({ theme }) => ({
  border: "1px solid #CC9966",
  borderRadius: "8px",
  height: "60px",
  background: "linear-gradient(90deg, #beab6c 0%, #d4c28a 100%)",
  display: "flex",
  alignItems: "center",
  padding: "0 20px",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  marginBottom: "20px",
}));

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: "20px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  borderRadius: "8px",
  borderLeft: "4px solid #1898c2",
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: "4px",
  padding: "8px 16px",
  textTransform: "none",
  fontWeight: 600,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  maxHeight: "300px",
  overflowY: "auto",
  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
  borderRadius: "8px",
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  "& .MuiTableCell-head": {
    backgroundColor: "#1898c2",
    color: "white",
    fontWeight: "bold",
    padding: "12px",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
}));

function AnnualPayment() {
  const [isOnlinePayment, setIsOnlinePayment] = useState(false);
  const [messageColor, setMessageColor] = useState("success");
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printPaymentId, setPrintPaymentId] = useState(0);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [paymentOption, setPaymentOption] = useState("annual");
  const [admissionNo, setAdmissionNo] = useState("");
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState("");
  const [feeDetails, setFeeDetails] = useState([]);
  const [calculationData, setCalculationData] = useState(null);
  const [banks, setBanks] = useState([]);
  const [paymentMode, setPaymentMode] = useState("");
  const [paidAmount, setPaidAmount] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [concessionAmount, setConcessionAmount] = useState(0);
  const [fineAmount, setFineAmount] = useState(0);
  const [transportFee, setTransportFee] = useState(0);
  const [inventoryBalance, setInventoryBalance] = useState(0);
  const [walletAmount, setWalletAmount] = useState(0);
  const [advanceAmount, setAdvanceAmount] = useState(0);
  const [savedPaymentId, setSavedPaymentId] = useState(0);
  const [receiptCopies, setReceiptCopies] = useState("one");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [hasDiscountBeenApplied, setHasDiscountBeenApplied] = useState(false);
  const [showDiscountDialog, setShowDiscountDialog] = useState(false);
  const [isConcessionApplied, setIsConcessionApplied] = useState(false);
  const [isDiscountDisabled, setIsDiscountDisabled] = useState(false);
  const [isDiscountDialogShown, setIsDiscountDialogShown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredStudents, setFilteredStudents] = useState(null);

  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_BASE_URL;
  const token = sessionStorage.getItem("token");

  const fetchDdlClass = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      setClasses(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching classes:", error);
      setClasses([]);
      setSnackbarMessage("Failed to fetch class data");
      setMessageColor("warning");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, token]);

  const fetchFinancialYears = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `${apiUrl}/ClassPromotion/GetFinancialYear`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      setFinancialYears(Array.isArray(data) ? data : []);
      const currentYear = new Date().getFullYear();
      const currentFinancialYear = data.find((year) =>
        year.finanacialYear.includes(`${currentYear}-${currentYear + 1}`)
      );
      if (currentFinancialYear) {
        setSelectedFinancialYear(currentFinancialYear.financialYearID);
      }
    } catch (error) {
      console.error("Error fetching financial years:", error);
      setFinancialYears([]);
      setSnackbarMessage("Failed to fetch financial years");
      setMessageColor("warning");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, token]);

  const fetchStudents = useCallback(
    async (classId) => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${apiUrl}/FeeDeposit/SerachAdmissionListAnual`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({ classId }),
          }
        );
        if (!response.ok) throw new Error(`Error: ${response.status}`);
        const data = await response.json();
        const uniqueStudents = Array.from(
          new Map(data.map((item) => [item.studentId, item])).values()
        );
        setStudents(Array.isArray(uniqueStudents) ? uniqueStudents : []);
      } catch (error) {
        console.error("Error fetching students:", error);
        setStudents([]);
        setSnackbarMessage("Failed to fetch student data");
        setMessageColor("warning");
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    },
    [apiUrl, token]
  );

  const fetchFeeDetails = useCallback(
    async (studentId, sessionId) => {
      try {
        setIsLoading(true);
        const response = await fetch(
          `${apiUrl}/FeeDeposit/GetAnualFeeDetails`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({ studentId, sessionId }),
          }
        );
        if (!response.ok) throw new Error(`Error: ${response.status}`);
        const data = await response.json();
        setFeeDetails(Array.isArray(data) ? data : []);
        // Check if a discount or concession has been applied
        const discountApplied = data.some(
          (fee) => (fee.discountAmount || 0) > 0
        );
        const concessionApplied = data.some(
          (fee) => (fee.concessionAmount || 0) > 0
        );
        setHasDiscountBeenApplied(discountApplied);
        setIsConcessionApplied(concessionApplied);
      } catch (error) {
        console.error("Error fetching fee details:", error);
        setFeeDetails([]);
        setHasDiscountBeenApplied(false);
        setIsConcessionApplied(false);
        setSnackbarMessage("Failed to fetch fee details");
        setMessageColor("warning");
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    },
    [apiUrl, token]
  );

  const fetchCalculationData = useCallback(
    async (studentId, sessionId) => {
      try {
        setIsLoading(true);
        const today = new Date();
        const receiptDate = `${today.getDate()}/${today.getMonth() + 1
          }/${today.getFullYear()}`;
        const response = await fetch(
          `${apiUrl}/FeeDeposit/GetAmountbyStudentId`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({
              studentId,
              monthId: 0,
              recieptDate: receiptDate,
            }),
          }
        );
        if (!response.ok) throw new Error(`Error: ${response.status}`);
        const data = await response.json();
        setCalculationData(data.objData || null);
        setTransportFee(parseFloat(data.objData?.transportFee) || 0);
        setConcessionAmount(parseFloat(data.objData?.concessionAmount) || 0);
        setDiscountAmount(0); // Always set to 0, ignoring API data
        setWalletAmount(parseFloat(data.objData?.walletAmount) || 0);
        setInventoryBalance(parseFloat(data.objData?.inveintoryBalance) || 0);
      } catch (error) {
        console.error("Error fetching calculation data:", error);
        setCalculationData(null);
        setSnackbarMessage("Failed to fetch calculation data");
        setMessageColor("warning");
        setSnackbarOpen(true);
      } finally {
        setIsLoading(false);
      }
    },
    [apiUrl, token]
  );

  const fetchBanks = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${apiUrl}/Bank/GetBank`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      setBanks(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching banks:", error);
      setBanks([]);
      setSnackbarMessage("Failed to fetch bank data");
      setMessageColor("warning");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchDdlClass();
    fetchFinancialYears();
    fetchBanks();
  }, [fetchDdlClass, fetchFinancialYears, fetchBanks]);

  useEffect(() => {
    if (selectedStudent && selectedFinancialYear) {
      fetchFeeDetails(selectedStudent.studentId, selectedFinancialYear);
      fetchCalculationData(selectedStudent.studentId, selectedFinancialYear);
    }
  }, [
    selectedStudent,
    selectedFinancialYear,
    fetchFeeDetails,
    fetchCalculationData,
  ]);

  const calculateTotalAnnualFee = () => {
    return (calculationData?.anualTotalAmount || 0) + (transportFee || 0);
  };

  const calculateAnnualBalance = () => {
    if (!calculationData) return 0;
    // Sum previous paid amounts and discounts from feeDetails
    const previousPaid = feeDetails.reduce(
      (sum, fee) => sum + (fee.paidAmount || 0),
      0
    );
    const previousDiscount = feeDetails.reduce(
      (sum, fee) => sum + (fee.discountAmount || 0),
      0
    );
    const balance =
      calculateTotalAnnualFee() -
      previousPaid -
      paidAmount -
      previousDiscount -
      discountAmount -
      concessionAmount +
      fineAmount;
    return balance;
  };

  const handlePaymentModeChange = (e) => {
  const selectedBankId = e.target.value;
  setPaymentMode(selectedBankId);
  const selectedBank = banks.find((bank) => bank.bankId === selectedBankId);
  setIsOnlinePayment(selectedBank?.bankName?.toLowerCase().includes("online"));
};

const handlePayOnline = async () => {
  if (!selectedStudent || paidAmount <= 0) {
    setSnackbarMessage("Please select a student and enter a valid amount.");
    setMessageColor("warning");
    setSnackbarOpen(true);
    return;
  }

  try {
    setIsLoading(true);

    const isLoaded = await loadRazorpayScript();
    if (!isLoaded || !window.Razorpay) {
      setSnackbarMessage("Failed to load payment gateway. Please check your connection.");
      setMessageColor("warning");
      setSnackbarOpen(true);
      setIsLoading(false);
      return;
    }

    const body = {
      studentId: selectedStudent.studentId,
      feeType: "Annual",
      monthId: 0,
      amount: parseFloat(paidAmount),
    };

    const response = await fetch(`${apiUrl}/Payments/CreateOnlineOrder`, {

      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`Failed to create online order: ${response.status}`);
    }

    const data = await response.json();

    // Setup Razorpay options
    const options = {
      key: data.keyId,
      amount: data.amountInPaise,
      currency: data.currency,
      name: sessionStorage.getItem("schoolName") || "School",
      description: "Annual Fee Payment",
      order_id: data.orderId,
      handler: function (razorpayResponse) {
        handleSubmit(razorpayResponse); // Pass Razorpay response to handleSubmit
      },
      prefill: {
        name: selectedStudent.studentName,
        email: selectedStudent.email || "",
        contact: selectedStudent.mobileNo || "",
      },
      theme: { color: "#1898c2" },
      modal: {
        ondismiss: function () {
          setIsLoading(false);
          setSnackbarMessage("Payment cancelled by user");
          setMessageColor("warning");
          setSnackbarOpen(true);
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  } catch (error) {
    console.error("Error initiating online payment:", error);
    setSnackbarMessage(`Failed to initiate online payment: ${error.message}`);
    setMessageColor("warning");
    setSnackbarOpen(true);
  } finally {
    setIsLoading(false);
  }
};


  const handlePaymentOptionChange = (event) => {
    const value = event.target.value;
    setPaymentOption(value);
    if (value === "partial") {
      navigate(-1);
    }
  };

  const handleSearchClick = () => {
    setShowStudentModal(true);
  };

  const handleClassChange = (event) => {
    const classId = event.target.value;
    setSelectedClass(classId);
    setSearchQuery(""); // Reset search query
    setFilteredStudents(null); // Reset filtered students
    if (classId) {
      fetchStudents(classId);
    } else {
      setStudents([]);
      setFilteredStudents(null);
    }
  };

  const handleStudentSelect = (student) => {
    setSelectedStudent(student);
    setAdmissionNo(student.admissionNo);
    setShowStudentModal(false);
    setIsSaved(false);
    setPaidAmount(0);
    setDiscountAmount(0);
    setConcessionAmount(0);
    setFineAmount(0);
    setTransportFee(0);
    setInventoryBalance(0);
    setWalletAmount(0);
    setAdvanceAmount(0);
    setPaymentMode("");
    setFeeDetails([]);
    setCalculationData(null);
    setHasDiscountBeenApplied(false);
    setIsConcessionApplied(false); // Reset concession status for new student
  };

  const handleFinancialYearChange = (event) => {
    setSelectedFinancialYear(event.target.value);
    setHasDiscountBeenApplied(false); // Reset discount status for new financial year
    setIsConcessionApplied(false); // Reset concession status for new financial year
  };

  const handlePaidAmountChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    setPaidAmount(Math.max(0, value)); // Ensure value is non-negative
  };

  const handleDiscountAmountChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    if (
      hasDiscountBeenApplied &&
      value > 0 &&
      !isDiscountDisabled &&
      !isDiscountDialogShown
    ) {
      setShowDiscountDialog(true);
      setIsDiscountDialogShown(true); // Mark dialog as shown for this payment
    }
    if (!isDiscountDisabled) {
      setDiscountAmount(Math.max(0, value)); // Ensure value is non-negative
    }
  };

  useEffect(() => {
    const balance = calculateAnnualBalance();
    if (balance < 0) {
      setSnackbarMessage(
        "Enter a valid amount, annual balance cannot go negative."
      );
      setMessageColor("warning");
      setSnackbarOpen(true);
    }
  }, [
    paidAmount,
    discountAmount,
    concessionAmount,
    fineAmount,
    calculationData,
    feeDetails,
    transportFee,
  ]);

  const handleDiscountDialogConfirm = () => {
    setShowDiscountDialog(false);
    setIsDiscountDisabled(false); // Ensure the textbox remains enabled
    // No need to update discountAmount here; it's already set in handleDiscountAmountChange
  };

  const handleDiscountDialogCancel = () => {
    setShowDiscountDialog(false);
    setDiscountAmount(0); // Reset discount amount
    setIsDiscountDisabled(true); // Disable the discount textbox
    setIsDiscountDialogShown(false); // Reset dialog shown state
  };

  const handleConcessionChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    setConcessionAmount(value);
  };

  const handleFineChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    setFineAmount(value);
  };

  const handleTransportFeeChange = (event) => {
    const value = parseFloat(event.target.value) || 0;
    setTransportFee(value);
  };

 const handleSubmit = async (razorpayResponse = null) => {
    if (!selectedStudent) {
      setSnackbarMessage("Please select a student before proceeding.");
      setMessageColor("warning");
      setSnackbarOpen(true);
      return;
    }
    if (!paymentMode) {
      setSnackbarMessage("Please select a payment mode.");
      setMessageColor("warning");
      setSnackbarOpen(true);
      return;
    }
    if (!calculationData) {
      setSnackbarMessage("No fee details available to proceed.");
      setMessageColor("warning");
      setSnackbarOpen(true);
      return;
    }
    const balance = calculateAnnualBalance();

    // Check if balance is negative due to invalid input
    if (balance < 0) {
      setSnackbarMessage(
        "Enter a valid amount, annual balance cannot go negative."
      );
      setMessageColor("warning");
      setSnackbarOpen(true);
      return;
    }
    // Check if balance is zero (all payments complete)
    if (balance === 0 && paidAmount <= 0) {
      setSnackbarMessage("All payments are complete.");
      setMessageColor("success");
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsLoading(true);
      const today = new Date();
      const paymentDate = `${today.getDate().toString().padStart(2, "0")}/${(
        today.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}/${today.getFullYear()}`;
      const payload = {
        studentId: selectedStudent.studentId,
        sessionId: selectedFinancialYear,
        paidAmount,
        discount: discountAmount,
        concessionAmount,
        fineAmount,
        balance: calculateAnnualBalance(),
        paymentMode:
          banks.find((bank) => bank.bankId === paymentMode)?.bankName || "",
        paymentModeId: paymentMode,
        paymentDate,
        walleteAmount: walletAmount,
        advanceAmount,
        feePaymentId: 0,
        paidStatus: true,
        month: "Annual",
        monthId: 0,
        checqueNo: "0",
        checqueDate: "01/01/2020",
        previousPaidAmount: calculationData?.paidAmount || 0,
      };

      const response = await fetch(`${apiUrl}/FeeDeposit/FeeDeposit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      if (data.objreponse.status !== "Succeed!") {
        throw new Error(data.objreponse.msg || "Failed to save payment");
      }
      setSavedPaymentId(data.paymentId);
      setIsSaved(true);
      setShowSuccessModal(true);

      // Reset form fields and state after successful payment
      setPaidAmount(0);
      setDiscountAmount(0);
      setConcessionAmount(0);
      setFineAmount(0);
      setTransportFee(0);
      setInventoryBalance(0);
      setWalletAmount(0);
      setAdvanceAmount(0);
      setPaymentMode("");
      setHasDiscountBeenApplied(false);
      setIsConcessionApplied(false);
      setIsDiscountDisabled(false);
      setIsDiscountDialogShown(false);

      // Re-fetch fee details and calculation data to update balance
      await fetchFeeDetails(selectedStudent.studentId, selectedFinancialYear);
      await fetchCalculationData(
        selectedStudent.studentId,
        selectedFinancialYear
      );
    } catch (error) {
      console.error("Error saving payment:", error);
      setSnackbarMessage("Failed to save payment. Please try again.");
      setMessageColor("warning");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBranchDetails = async () => {
    try {
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      return Array.isArray(data) && data.length > 0 ? data[0] : {};
    } catch (error) {
      console.error("Error fetching branch details:", error);
      setSnackbarMessage("Failed to fetch branch details");
      setMessageColor("warning");
      setSnackbarOpen(true);
      return {};
    }
  };

  const generateReceipt = async (paymentId = savedPaymentId) => {
    if (!paymentId) {
      setSnackbarMessage("No payment ID available for receipt generation.");
      setMessageColor("warning");
      setSnackbarOpen(true);
      return;
    }

    try {
      setIsLoading(true);

      const response = await fetch(`${apiUrl}/FeeDeposit/AnualFeeReciept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ paymentId }),
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);
      const data = await response.json();
      if (!data?.length) throw new Error("Receipt data is empty.");

      const receiptData = data[0];
      const branchData = await fetchBranchDetails();
      const schoolName =
        sessionStorage.getItem("schoolName") ||
        branchData?.organisationName ||
        "Jeevan Adarsh Vidyalaya";
      const logoBase64 = branchData.bytLogo
        ? `data:image/jpeg;base64,${branchData.bytLogo}`
        : null;
      const address = branchData.branchAddress || "Not Available";
      const financialYear =
        financialYears.find((year) => year.financialYearID === selectedFinancialYear)
          ?.finanacialYear || "N/A";

      const generateSingleReceipt = (doc, isCopy = false, isFirstReceipt = true) => {
        doc.setFont("helvetica", "normal");
        doc.setTextColor(0, 0, 0);

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 4;
        const contentWidth = (pageWidth - 3 * margin) / 2;
        const startX = isFirstReceipt ? margin : margin * 2 + contentWidth;
        let yPos = margin + 2;

        doc.setLineWidth(0.5);
        doc.setDrawColor(50, 50, 50);
        doc.rect(startX, margin, contentWidth, pageHeight - 2 * margin);

        if (logoBase64) {
          try {
            const logoWidth = 14;
            const imgProps = doc.getImageProperties(logoBase64);
            const aspectRatio = imgProps.width / imgProps.height;
            const calculatedHeight = logoWidth / aspectRatio;
            doc.addImage(logoBase64, "JPEG", startX + 3, yPos, logoWidth, calculatedHeight);
          } catch (error) {
            console.error("Error adding logo to PDF:", error);
            doc.setFontSize(6.5);
            doc.text("No Logo Available", startX + 3, yPos + 8);
          }
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        const schoolNameLines = doc.splitTextToSize(schoolName, contentWidth - 20);
        doc.text(schoolNameLines, startX + 20, yPos + 3);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        const addressLines = doc.splitTextToSize(
          `Address: ${address}`,
          contentWidth - 20
        );
        doc.text(addressLines, startX + 20, yPos + 8);
        yPos += 10 + addressLines.length * 3;
        doc.text(`Email: ${branchData.emailID || "Not Available"}`, startX + 20, yPos);
        yPos += 3.5;
        doc.text(`Phone: ${branchData.mobileNo || "Not Available"}`, startX + 20, yPos);

        yPos += 6;
        doc.setFontSize(7);
        doc.setFont("helvetica", "bold");
        doc.text(`ANNUAL FEE RECEIPT${isCopy ? " (Copy)" : ""}`, startX + contentWidth / 2, yPos, {
          align: "center",
        });

        yPos += 4;
        doc.setLineWidth(0.2);
        doc.setDrawColor(150, 150, 150);
        doc.line(startX + 2, yPos, startX + contentWidth - 2, yPos);

        yPos += 3;
        doc.setFontSize(6.5);
        doc.setFont("helvetica", "bold");
        doc.text("Student Information", startX + 2, yPos);
        doc.setFont("helvetica", "normal");

        yPos += 4;
        doc.text(`Receipt No: ${receiptData.receiptNo || "N/A"}`, startX + 2, yPos);
        doc.text(`Date: ${receiptData.feePaymentDate || "N/A"}`, startX + contentWidth / 2, yPos);
        yPos += 4;
        doc.text(`Student Name: ${receiptData.studentName || "N/A"}`, startX + 2, yPos);
        doc.text(`Admission No: ${receiptData.admissionNo || "N/A"}`, startX + contentWidth / 2, yPos);
        yPos += 4;
        doc.text(`Class: ${calculationData?.className || "N/A"}`, startX + 2, yPos);
        doc.text(`Financial Year: ${financialYear}`, startX + contentWidth / 2, yPos);

        yPos += 5;
        doc.line(startX + 2, yPos, startX + contentWidth - 2, yPos);
        yPos += 3;

        const tableBody = [
          ["Annual Fee", `Rs. ${(receiptData.totalAmount || 0).toFixed(2)}`],
          ["Transport Fee", `Rs. ${(transportFee || 0).toFixed(2)}`],
          ["Fine Amount", `Rs. ${(fineAmount || 0).toFixed(2)}`],
          ["Total Charges", `Rs. ${((receiptData.totalAmount || 0) + (transportFee || 0)).toFixed(2)}`],
          ["Concession (-)", `Rs. ${(concessionAmount || 0).toFixed(2)}`],
          ["Discount (-)", `Rs. ${(receiptData.discountAmount || 0).toFixed(2)}`],
          ["Pay Amount", `Rs. ${(receiptData.paidAmount || 0).toFixed(2)}`],
          ["Balance", `Rs. ${(receiptData.balanceAmount || 0).toFixed(2)}`],
        ];

        doc.autoTable({
          startY: yPos,
          startX: startX + 2,
          head: [["Description", "Amount"]],
          body: tableBody,
          theme: "grid",
          styles: {
            fontSize: 6.5,
            cellPadding: 1.2,
            textColor: [0, 0, 0],
            lineColor: [100, 100, 100],
            lineWidth: 0.1,
          },
          headStyles: {
            fillColor: [24, 152, 194],
            textColor: [255, 255, 255],
            fontSize: 7,
            fontStyle: "bold",
            halign: "center",
          },
          columnStyles: {
            0: { cellWidth: contentWidth * 0.6, halign: "left" },
            1: { cellWidth: contentWidth * 0.3, halign: "right" },
          },
          margin: { left: startX + 2, right: startX + 4 },
          didParseCell: function (data) {
            if (data.row.section === 'body' && data.row.raw[0] === "Total Charges") {
              data.cell.styles.fillColor = [173, 216, 230];
              data.cell.styles.fontStyle = "bold";
            }
          },
        });

        yPos = doc.lastAutoTable.finalY + 4;
        doc.setFontSize(6.5);
        doc.text("Authorized Signatory: ____________________", startX + contentWidth - 2, yPos, {
          align: "right",
        });

        yPos += 4;
        doc.setFont("helvetica", "italic");
        doc.text(
          "This is a computer-generated receipt and does not require a signature.",
          startX + contentWidth / 2,
          yPos,
          { align: "center" }
        );
      };

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a5",
      });

      if (receiptCopies === "one") {
        generateSingleReceipt(doc, false, true);
      } else {
        generateSingleReceipt(doc, false, true);
        generateSingleReceipt(doc, true, false);
      }

      const pdfBlob = doc.output("blob");
      if (!pdfBlob || !(pdfBlob instanceof Blob)) {
        throw new Error("Failed to generate valid PDF blob.");
      }

      const pdfUrl = URL.createObjectURL(pdfBlob);
      try {
        const newWindow = window.open(pdfUrl, "_blank");
        if (!newWindow) {
          throw new Error("Browser blocked new tab.");
        }
      } catch (error) {
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = `Annual_Fee_Receipt_${receiptData.receiptNo || "Unknown"}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setSnackbarMessage("Unable to open PDF in new tab. File downloaded instead.");
        setMessageColor("warning");
        setSnackbarOpen(true);
      } finally {
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 100);
      }

      // Cleanup
      setShowSuccessModal(false);
      setSavedPaymentId(0);
      setReceiptCopies("one");
      setIsSaved(false);
      setSnackbarMessage("Receipt generated successfully.");
      setMessageColor("success");
      setSnackbarOpen(true);

      // Reset Form
      setPaidAmount(0);
      setDiscountAmount(0);
      setConcessionAmount(0);
      setFineAmount(0);
      setTransportFee(0);
      setInventoryBalance(0);
      setWalletAmount(0);
      setAdvanceAmount(0);
      setPaymentMode("");
      setHasDiscountBeenApplied(false);
      setIsDiscountDisabled(false);
      setIsDiscountDialogShown(false);

      if (selectedStudent && selectedFinancialYear) {
        await fetchFeeDetails(selectedStudent.studentId, selectedFinancialYear);
        await fetchCalculationData(selectedStudent.studentId, selectedFinancialYear);
      }
    } catch (error) {
      console.error("Error generating receipt:", error);
      setSnackbarMessage("Failed to generate receipt.");
      setMessageColor("warning");
      setSnackbarOpen(true);
    } finally {
      setIsLoading(false);
    }
  };





  const handleCancel = () => {
    setSelectedStudent(null);
    setAdmissionNo("");
    setStudents([]);
    setSelectedClass("");
    setIsSaved(false);
    setFeeDetails([]);
    setCalculationData(null);
    setPaidAmount(0);
    setDiscountAmount(0);
    setConcessionAmount(0);
    setFineAmount(0);
    setTransportFee(0);
    setInventoryBalance(0);
    setWalletAmount(0);
    setAdvanceAmount(0);
    setPaymentMode("");
    setHasDiscountBeenApplied(false);
    setIsDiscountDisabled(false); // Reset discount disabled state
    setIsDiscountDialogShown(false); // Reset dialog shown state
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <>
      <StyledContainer>
        {isLoading && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0,0,0,0.3)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        <StyledHeader>
          <RadioGroup
            row
            value={paymentOption}
            onChange={handlePaymentOptionChange}
          >
            <FormControlLabel
              value="partial"
              control={<Radio />}
              label="Monthly Payment"
              sx={{
                "& .MuiFormControlLabel-label": {
                  color: "white",
                  fontWeight: "600",
                },
              }}
            />
            {/* <FormControlLabel
              value="annual"
              control={<Radio />}
              label="Annual Pack Payment"
              sx={{
                "& .MuiFormControlLabel-label": {
                  color: "white",
                  fontWeight: "600",
                },
              }}
            /> */}
          </RadioGroup>
          <Tooltip title="View User Guide">
            <IconButton
              onClick={() => setShowUserGuide(true)}
              sx={{ color: "white", marginLeft: "auto" }}
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </StyledHeader>

        <Dialog
          open={showUserGuide}
          onClose={() => setShowUserGuide(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              backgroundColor: "#f9fafb",
            },
          }}
        >
          <DialogTitle
            sx={{
              background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)",
              color: "white",
              fontWeight: "bold",
              textAlign: "center",
              borderRadius: "12px 12px 0 0",
              py: 2,
            }}
          >
            Fee Deposit User Guide
          </DialogTitle>
          <DialogContent sx={{ padding: "24px" }}>
            <Typography
              variant="h6"
              sx={{
                color: "#1898c2",
                fontWeight: "600",
                mb: 2,
                mt: 1,
                textAlign: "center",
              }}
            >
              Quick Steps to Process Fee Payments
            </Typography>
            <Box
              component="ul"
              sx={{
                listStyleType: "none",
                padding: 0,
                "& li": {
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 1.5,
                  fontSize: "0.9rem",
                  color: "#333",
                },
                "& li::before": {
                  content: '"\\2022"', // Bullet point
                  color: "#1898c2",
                  fontWeight: "bold",
                  display: "inline-block",
                  width: "1.5em",
                  mr: 1,
                  fontSize: "1.2rem",
                },
                "& strong": {
                  margin: "0 0.2em", // Add spacing around strong tags
                },
              }}
            >
              <li>
                Choose the <strong>"Annual Pack Payment"</strong> option from
                the header.
              </li>
              <li>
                Click <strong>"Select Student"</strong> to choose a student by
                class or admission number.
              </li>
              <li>
                Select a <strong>financial year</strong> from the dropdown to
                view fee details.
              </li>
              <li>
                Review the <strong>"Annual Payment Details"</strong> table for
                the selected student.
              </li>
              <li>
                Adjust the <strong>"Fine Amount"</strong> if necessary in the
                "Fee Calculation" section.
              </li>
              <li>
                Apply a <strong>Discount</strong> if needed, confirming any
                additional discounts in the dialog.
              </li>
              <li>
                Enter the <strong>"Paid Amount"</strong> and select a payment
                mode.
              </li>
              <li>
                Click <strong>"Save"</strong> to process the payment.
              </li>
              <li>
                Click <strong>"Generate Receipt"</strong> to download the fee
                receipt.
              </li>
              <li>
                Use the <strong>"Cancel"</strong> button to reset the form or wish to proceed for other student.
              </li>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
            <StyledButton
              onClick={() => setShowUserGuide(false)}
              color="primary"
              variant="contained"
              sx={{
                backgroundColor: "#1898c2",
                "&:hover": { backgroundColor: "#156f9a" },
                borderRadius: "8px",
                px: 4,
              }}
            >
              Close
            </StyledButton>
          </DialogActions>
        </Dialog>

        <Box sx={{ marginBottom: "20px" }}>
          <StyledButton
            variant="contained"
            color="primary"
            onClick={handleSearchClick}
            disabled={isLoading || isSaved}
          >
            Select Student
          </StyledButton>
        </Box>

        <Box sx={{ marginBottom: "20px" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              color: "#1898c2",
              marginBottom: "16px",
              textAlign: "center",
            }}
          >
            Student Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Admission No."
                value={selectedStudent ? selectedStudent.admissionNo : "N/A"}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleSearchClick}
                        disabled={isSaved}
                      >
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Student Name"
                value={
                  selectedStudent
                    ? selectedStudent.studentName
                    : "Select a student"
                }
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Class"
                value={selectedStudent ? selectedStudent.className : "N/A"}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Section"
                value={selectedStudent ? selectedStudent.sectionName : "N/A"}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Roll No."
                value={selectedStudent ? selectedStudent.rollNo : "N/A"}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Father's Name"
                value={selectedStudent ? selectedStudent.fatherName : "N/A"}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </Box>

        {selectedStudent && (
          <Box sx={{ marginBottom: "40px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: "#1898c2",
                    marginBottom: "16px",
                    textAlign: "center",
                  }}
                >
                  Annual Payment Details
                </Typography>
                <FormControl
                  fullWidth
                  sx={{ marginBottom: "20px", maxWidth: 300 }}
                >
                  <Select
                    value={selectedFinancialYear}
                    onChange={handleFinancialYearChange}
                    displayEmpty
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: "4px" }}
                  >
                    <MenuItem value="">Select Financial Year</MenuItem>
                    {financialYears.map((year) => (
                      <MenuItem
                        key={year.financialYearID}
                        value={year.financialYearID}
                      >
                        {year.finanacialYear}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <StyledTableContainer component={Paper}>
                  <Table stickyHeader aria-label="annual payment details table">
                    <StyledTableHead>
                      <TableRow>
                        <TableCell>Receipt No</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Paid Amount</TableCell>
                        <TableCell>Discount Amount</TableCell>
                        <TableCell>Payment Mode</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Print</TableCell>
                      </TableRow>
                    </StyledTableHead>
                    <TableBody>
                      {feeDetails.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            sx={{ textAlign: "center", padding: "20px" }}
                          >
                            <Typography variant="body1" color="text.secondary">
                              No payment details found
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        feeDetails.map((fee) => (
                          <TableRow
                            key={fee.anualPaymentId}
                            sx={{
                              backgroundColor:
                                fee.paidAmount > 0 ? "#e8f5e9" : "#fff",
                              "&:hover": { backgroundColor: "#e0f7fa" },
                            }}
                          >
                            <TableCell>{fee.recieptNo}</TableCell>
                            <TableCell>{fee.date}</TableCell>
                            <TableCell>
                              ₹{(fee.paidAmount || 0).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              ₹{(fee.discountAmount || 0).toFixed(2)}
                            </TableCell>
                            <TableCell>{fee.paymentMode}</TableCell>
                            <TableCell>
                              {fee.paidAmount > 0 ? "Paid" : "Unpaid"}
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Print Receipt">
                                <IconButton
                                  onClick={() => {
                                    setPrintPaymentId(fee.anualPaymentId);
                                    setShowPrintModal(true);
                                  }}
                                  disabled={fee.paidAmount === 0}
                                >
                                  <PrintIcon />
                                </IconButton>
                              </Tooltip>

                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </StyledTableContainer>
              </Grid>
              <Grid item xs={12} md={6}>
                <StyledCard>
  <CardContent>
    <Typography
      variant="h6"
      sx={{
        fontWeight: "bold",
        color: "#1898c2",
        marginBottom: "16px",
      }}
    >
      Fee Calculation
    </Typography>

    <Grid container spacing={2}>
      {/* Row 1: Base Amounts */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Total Annual Fee"
          value={calculateTotalAnnualFee().toFixed(2)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                ₹
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Transport Fee"
          type="number"
          value={transportFee}
          onChange={handleTransportFeeChange}
          variant="outlined"
          size="small"
          fullWidth
          disabled={isSaved}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                ₹
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {/* Row 2: Additional Charges */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Fine Amount"
          type="number"
          value={fineAmount}
          onChange={handleFineChange}
          variant="outlined"
          size="small"
          fullWidth
          disabled={isLoading || isSaved}
          inputProps={{
            style: {
              MozAppearance: 'textfield'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                ₹
              </InputAdornment>
            ),
            sx: {
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                display: 'none'
              },
              '& input': {
                MozAppearance: 'textfield'
              }
            }
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Inventory Balance"
          value={(Number(inventoryBalance) || 0).toFixed(2)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                ₹
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {/* Row 3: Deductions */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Concession Amount"
          type="number"
          value={concessionAmount}
          variant="outlined"
          size="small"
          fullWidth
          disabled={isLoading || isSaved || isConcessionApplied}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                ₹
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Discount Amount"
          type="number"
          value={discountAmount}
          onChange={handleDiscountAmountChange}
          variant="outlined"
          size="small"
          fullWidth
          disabled={isLoading || isSaved || isDiscountDisabled}
          inputProps={{
            min: 0,
            style: {
              MozAppearance: 'textfield'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                ₹
              </InputAdornment>
            ),
            sx: {
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                display: 'none'
              },
              '& input': {
                MozAppearance: 'textfield'
              }
            }
          }}
        />
      </Grid>

      {/* Row 4: Available Resources */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Advance Amount"
          value={(Number(advanceAmount) || 0).toFixed(2)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                ₹
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Wallet Amount"
          value={(Number(walletAmount) || 0).toFixed(2)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                ₹
              </InputAdornment>
            ),
          }}
        />
      </Grid>

      {/* Row 5: Payment Details */}
      <Grid item xs={12} sm={6}>
        <TextField
          label="Pay Amount"
          type="number"
          value={paidAmount}
          onChange={handlePaidAmountChange}
          variant="outlined"
          size="small"
          fullWidth
          required
          disabled={isLoading || isSaved}
           sx={{
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#eff9fbff',
      fontWeight: 'bold',
    }
  }}
          inputProps={{
            min: 0,
            style: {
              MozAppearance: 'textfield'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                ₹
              </InputAdornment>
            ),
            sx: {
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                display: 'none'
              },
              '& input': {
                MozAppearance: 'textfield'
              }
            }
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <Select
            value={paymentMode}
            onChange={handlePaymentModeChange}
            displayEmpty
            variant="outlined"
            size="small"
            disabled={isSaved}
          >
            <MenuItem value="">Select Payment Mode</MenuItem>
            {banks.map((bank) => (
              <MenuItem key={bank.bankId} value={bank.bankId}>
                {bank.bankName}{" "}
                {bank.accountNo ? `(${bank.accountNo})` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Row 6: Final Balance */}
      <Grid item xs={12}>
        <TextField
          label="Annual Balance"
          value={calculateAnnualBalance().toFixed(2)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                ₹
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#fff3e0',
              fontWeight: 'bold',
            }
          }}
        />
      </Grid>
    </Grid>
  </CardContent>
</StyledCard>
              </Grid>
            </Grid>
          </Box>
        )}

     <Box sx={{ textAlign: "center", marginTop: "20px" }}>
  {selectedStudent && calculateAnnualBalance() === 0 && paidAmount <= 0 && (
    <Alert severity="success" sx={{ marginBottom: "10px" }}>
      All payments are complete.
    </Alert>
  )}
  {!isOnlinePayment ? (
    <StyledButton
      variant="contained"
      color="primary"
      onClick={handleSubmit}
      disabled={
        !selectedStudent ||
        isSaved ||
        isLoading ||
        paidAmount <= 0 ||
        calculateAnnualBalance() < 0
      }
      sx={{ marginRight: "10px" }}
    >
      Save
    </StyledButton>
  ) : (
    <StyledButton
      variant="contained"
      color="primary"
      onClick={handlePayOnline}
      disabled={
        !selectedStudent ||
        isSaved ||
        isLoading ||
        paidAmount <= 0 ||
        calculateAnnualBalance() < 0
      }
      sx={{ marginRight: "10px" }}
    >
      Pay Online
    </StyledButton>
  )}
  <StyledButton
    variant="contained"
    color="error"
    onClick={handleCancel}
    disabled={!selectedStudent && !isSaved}
    sx={{ marginRight: "10px" }}
  >
    Cancel
  </StyledButton>
  <StyledButton
    onClick={() => generateReceipt(savedPaymentId)}
    color="primary"
    variant="contained"
    disabled={!isSaved || isLoading}
  >
    Generate Receipt
  </StyledButton>
</Box>

      </StyledContainer>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={messageColor}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <BootstrapModal
        show={showStudentModal}
        onHide={() => setShowStudentModal(false)}
        centered
        dialogClassName="custom-modal"
      >
        <BootstrapModal.Header closeButton>
          <BootstrapModal.Title>Select Student</BootstrapModal.Title>
        </BootstrapModal.Header>
        <BootstrapModal.Body>
          <Grid container spacing={2} sx={{ marginBottom: "20px" }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <Select
                  value={selectedClass}
                  onChange={handleClassChange}
                  displayEmpty
                  variant="outlined"
                  size="small"
                  sx={{ borderRadius: "4px" }}
                >
                  <MenuItem value="">Select Class</MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls.classId} value={cls.classId}>
                      {cls.className}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Search by Name or Admission No."
                variant="outlined"
                size="small"
                fullWidth
                onChange={(e) => {
                  const query = e.target.value.toLowerCase();
                  setSearchQuery(query);
                  const filtered = students.filter(
                    (student) =>
                      student.studentName.toLowerCase().includes(query) ||
                      student.admissionNo.toLowerCase().includes(query)
                  );
                  setFilteredStudents(filtered);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 12px rgba(24, 152, 194, 0.2)",
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
          <TableContainer
            component={Paper}
            sx={{ maxHeight: "400px", overflowY: "auto" }}
          >
            <Table stickyHeader aria-label="student selection table">
              <TableHead>
                <TableRow>
                  <TableCell>Admission No.</TableCell>
                  <TableCell>Student Name</TableCell>
                  <TableCell>Class</TableCell>
                  <TableCell>Section</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(filteredStudents || students).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>No students found</TableCell>
                  </TableRow>
                ) : (
                  (filteredStudents || students).map((student) => (
                    <TableRow
                      key={student.studentId}
                      sx={{
                        "&:hover": { backgroundColor: "#e0f7fa" },
                        transition: "background-color 0.2s ease-in-out",
                      }}
                    >
                      <TableCell>{student.admissionNo}</TableCell>
                      <TableCell>{student.studentName}</TableCell>
                      <TableCell>{student.className}</TableCell>
                      <TableCell>{student.sectionName}</TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleStudentSelect(student)}
                          sx={{
                            transition: "transform 0.2s ease-in-out",
                            "&:hover": {
                              transform: "scale(1.05)",
                            },
                          }}
                        >
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </BootstrapModal.Body>
      </BootstrapModal>

      <Dialog
        open={showDiscountDialog}
        onClose={handleDiscountDialogCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Additional Discount</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            A discount has already been given to this student. Do you wish to
            give a further discount for this payment?
          </Typography>
        </DialogContent>
        <DialogActions>
          <StyledButton
            onClick={handleDiscountDialogCancel}
            color="error"
            variant="contained"
          >
            No
          </StyledButton>
          <StyledButton
            onClick={handleDiscountDialogConfirm}
            color="primary"
            variant="contained"
          >
            Yes
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Payment Successful</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Your annual fee deposit was successful. Click the button below to
            generate the receipt.
          </Typography>
          <RadioGroup
            value={receiptCopies}
            onChange={(e) => setReceiptCopies(e.target.value)}
            row
            sx={{ marginTop: 2 }}
          >
            <FormControlLabel
              value="one"
              control={<Radio />}
              label="Generate One Copy"
            />
            <FormControlLabel
              value="two"
              control={<Radio />}
              label="Generate Two Copies"
            />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <StyledButton
            onClick={() => setShowSuccessModal(false)}
            color="error"
            variant="contained"
          >
            Cancel
          </StyledButton>
          <StyledButton
            onClick={() => generateReceipt(savedPaymentId)}
            color="primary"
            variant="contained"
          >
            Generate Receipt
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Receipt</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Select the number of copies to generate.
          </Typography>
          <RadioGroup
            value={receiptCopies}
            onChange={(e) => setReceiptCopies(e.target.value)}
            row
            sx={{ marginTop: 2 }}
          >
            <FormControlLabel value="one" control={<Radio />} label="Generate One Copy" />
            <FormControlLabel value="two" control={<Radio />} label="Generate Two Copies" />
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <StyledButton
            onClick={() => setShowPrintModal(false)}
            color="error"
            variant="contained"
          >
            Cancel
          </StyledButton>
          <StyledButton
            onClick={() => {
              generateReceipt(printPaymentId);
              setShowPrintModal(false);
            }}
            color="primary"
            variant="contained"
          >
            Generate Receipt
          </StyledButton>
        </DialogActions>
      </Dialog>

    </>
  );
}

export default AnnualPayment;
